import React, { useEffect, useReducer, useRef } from "react";
import {
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSectionEditor } from "./SectionEditorContext";

export const createInitialMathState = (metadata = {}) => ({
  prompt: metadata.prompt || "",
  latex: metadata.latex || "",
  workspaceDescription: metadata.workspaceDescription || "",
  showWorkspaceDescription:
    metadata.showWorkspaceDescription == null
      ? true
      : Boolean(metadata.showWorkspaceDescription),
  allowFileUpload: Boolean(metadata.allowFileUpload),
  rubric: metadata.rubric || "",
});

export const mathWorkReducer = (state, action) => {
  switch (action.type) {
    case "HYDRATE": {
      if (!action.payload || typeof action.payload !== "object") {
        return state;
      }
      const nextState = createInitialMathState(action.payload);
      const currentSerialized = JSON.stringify(state);
      const nextSerialized = JSON.stringify(nextState);
      return currentSerialized === nextSerialized ? state : nextState;
    }
    case "SET_PROMPT":
      return { ...state, prompt: action.value || "" };
    case "SET_LATEX":
      return { ...state, latex: action.value || "" };
    case "SET_WORKSPACE_DESCRIPTION":
      return { ...state, workspaceDescription: action.value || "" };
    case "TOGGLE_WORKSPACE":
      return {
        ...state,
        showWorkspaceDescription: !state.showWorkspaceDescription,
      };
    case "TOGGLE_UPLOAD":
      return { ...state, allowFileUpload: !state.allowFileUpload };
    case "SET_RUBRIC":
      return { ...state, rubric: action.value || "" };
    default:
      return state;
  }
};

const computeMathErrors = (state) => {
  const errors = {};
  if (!state.prompt || !state.prompt.trim()) {
    errors.prompt = "Enter the problem prompt.";
  }

  if (!state.workspaceDescription && state.showWorkspaceDescription) {
    errors.workspaceDescription = "Describe what learners should show.";
  }

  return Object.keys(errors).length ? errors : null;
};

const MathWorkSectionEditor = () => {
  const { section, updateMetadata, setSectionErrors, sectionErrors } =
    useSectionEditor();
  const [state, dispatch] = useReducer(
    mathWorkReducer,
    section.metadata,
    createInitialMathState
  );
  const serializedRef = useRef(JSON.stringify(state));
  const lastErrorRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "HYDRATE", payload: section.metadata });
  }, [section.metadata]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serializedRef.current !== serialized) {
      serializedRef.current = serialized;
      updateMetadata({ ...state });
    }
  }, [state, updateMetadata]);

  useEffect(() => {
    const errors = computeMathErrors(state);
    const serializedErrors = JSON.stringify(errors || {});
    if (serializedErrors !== lastErrorRef.current) {
      lastErrorRef.current = serializedErrors;
      setSectionErrors(errors);
    }
  }, [state, setSectionErrors]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" component="h3">
            Show your work
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Craft prompts with LaTeX support and guide learners on how to
            demonstrate their reasoning.
          </Typography>
        </Box>

        <TextField
          label="Prompt"
          value={state.prompt}
          onChange={(event) =>
            dispatch({ type: "SET_PROMPT", value: event.target.value })
          }
          multiline
          minRows={3}
          helperText={sectionErrors?.prompt}
          error={Boolean(sectionErrors?.prompt)}
        />

        <TextField
          label="LaTeX / math markup"
          value={state.latex}
          onChange={(event) =>
            dispatch({ type: "SET_LATEX", value: event.target.value })
          }
          placeholder="e.g. a^2 + b^2 = c^2"
        />

        <FormControlLabel
          control={
            <Switch
              checked={state.showWorkspaceDescription}
              onChange={() => dispatch({ type: "TOGGLE_WORKSPACE" })}
            />
          }
          label="Provide workspace guidance"
        />

        {state.showWorkspaceDescription && (
          <TextField
            label="Workspace description"
            value={state.workspaceDescription}
            onChange={(event) =>
              dispatch({
                type: "SET_WORKSPACE_DESCRIPTION",
                value: event.target.value,
              })
            }
            multiline
            minRows={3}
            helperText={sectionErrors?.workspaceDescription}
            error={Boolean(sectionErrors?.workspaceDescription)}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={state.allowFileUpload}
              onChange={() => dispatch({ type: "TOGGLE_UPLOAD" })}
            />
          }
          label="Allow learners to attach supporting files"
        />

        <TextField
          label="Rubric or scoring guidance"
          value={state.rubric}
          onChange={(event) =>
            dispatch({ type: "SET_RUBRIC", value: event.target.value })
          }
          multiline
          minRows={3}
        />
      </Stack>
    </Paper>
  );
};

export default MathWorkSectionEditor;
